/* Veer PDP gallery — mobile upgrades (2026-07-10, work/mobile-polish)
   1) Dot indicators for the mobile media slider, synced to Dawn's base
      SliderComponent via its `slideChanged` CustomEvent (global.js L780).
      Dawn only wires dots for SlideshowComponent; the product gallery uses
      the base class, so we render + sync our own. Counter ("3 / 8") is
      Dawn-native — we only unhide it (mobile_thumbnails: hide).
   2) Pinch + double-tap zoom inside the product media lightbox
      (<product-modal>). Touch events (not pointer events) on purpose:
      non-passive touchmove + preventDefault works on iOS Safari AND
      Android Chrome without the touch-action pre-declaration trap.
      Single-finger scroll through the modal stays native until zoomed.
   Zero Dawn-core JS edits. L2 rules honoured: no innerHTML with dynamic
   data, all listeners guarded, state validated. */
(() => {
  'use strict';

  /* ---------- 1) mobile gallery dots ---------- */
  function initDots(gallery) {
    if (gallery.dataset.veerDots) return;
    const buttonsRow = gallery.querySelector('.slider-buttons');
    const slider = gallery.querySelector('.slider');
    const totalEl = gallery.querySelector('.slider-counter--total');
    const total = totalEl ? parseInt(totalEl.textContent, 10) : 0;
    // dots stop working as a scent past ~8 slides (2026-07-10 audit: a 22-media
    // product rendered 22 unusable 7px dots) — counter-only above the cap
    if (!buttonsRow || !slider || !Number.isInteger(total) || total < 2 || total > 8) return;
    gallery.dataset.veerDots = 'true';

    const dots = document.createElement('div');
    dots.className = 'veer-dots';
    dots.setAttribute('role', 'tablist');
    for (let i = 1; i <= total; i++) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'veer-dot';
      b.setAttribute('aria-label', 'Imaginea ' + i + ' din ' + total);
      b.addEventListener('click', () => {
        const items = gallery.sliderItemsToShow;
        const target = items && items[i - 1];
        const left = target ? target.offsetLeft : slider.clientWidth * (i - 1);
        slider.scrollTo({ left: left, behavior: 'smooth' });
      });
      dots.append(b);
    }
    // between the prev arrow and the counter reads jumbled — put dots first in the row
    buttonsRow.prepend(dots);

    const setActive = (page) => {
      const all = dots.children;
      for (let i = 0; i < all.length; i++) {
        all[i].classList.toggle('veer-dot--active', i === page - 1);
      }
    };
    setActive(1);
    gallery.addEventListener('slideChanged', (e) => {
      const p = e.detail && e.detail.currentPage;
      if (Number.isInteger(p) && p >= 1 && p <= total) setActive(p);
    });
  }

  /* ---------- 2) lightbox pinch + double-tap zoom ---------- */
  const MAX_SCALE = 4;
  const DBL_MS = 300;
  const DBL_PX = 30;

  function initZoom(modal) {
    if (modal.dataset.veerZoom) return;
    const content = modal.querySelector('.product-media-modal__content');
    if (!content) return;
    modal.dataset.veerZoom = 'true';

    let img = null; // image currently being manipulated
    const st = { scale: 1, x: 0, y: 0 };
    let start = null; // gesture-start snapshot
    let lastTap = { t: 0, x: 0, y: 0 };

    const apply = () => {
      if (!img) return;
      img.style.transform =
        st.scale > 1 ? 'translate3d(' + st.x + 'px,' + st.y + 'px,0) scale(' + st.scale + ')' : '';
      img.style.transformOrigin = '0 0';
    };
    const clampPan = () => {
      const w = img.clientWidth, h = img.clientHeight;
      st.x = Math.min(0, Math.max(w - w * st.scale, st.x));
      st.y = Math.min(0, Math.max(h - h * st.scale, st.y));
    };
    const reset = () => {
      if (img) { st.scale = 1; st.x = 0; st.y = 0; apply(); }
      img = null;
      start = null;
    };
    const setImg = (el) => {
      if (img && img !== el) { st.scale = 1; st.x = 0; st.y = 0; apply(); }
      img = el;
    };
    const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    const mid = (t) => ({ x: (t[0].clientX + t[1].clientX) / 2, y: (t[0].clientY + t[1].clientY) / 2 });

    content.addEventListener('touchstart', (e) => {
      const target = e.target.closest('img');
      if (e.touches.length === 2) {
        if (!target && !img) return;
        if (target) setImg(target);
        // layout origin = rendered origin minus current translate (origin 0 0,
        // transform = translate ∘ scale, so rendered_left = layout_left + t.x)
        const r = img.getBoundingClientRect();
        start = {
          d: dist(e.touches), m: mid(e.touches),
          scale: st.scale, x: st.x, y: st.y,
          lx: r.left - st.x, ly: r.top - st.y,
        };
      } else if (e.touches.length === 1 && img && st.scale > 1) {
        start = { m: { x: e.touches[0].clientX, y: e.touches[0].clientY }, scale: st.scale, x: st.x, y: st.y };
      }
    }, { passive: true });

    content.addEventListener('touchmove', (e) => {
      if (!img || !start) return;
      if (e.touches.length === 2) {
        e.preventDefault(); // custom pinch, not page zoom / modal scroll
        const m = mid(e.touches);
        const ns = Math.min(MAX_SCALE, Math.max(1, start.scale * (dist(e.touches) / start.d)));
        // keep the image point under the pinch midpoint anchored
        const qx = (start.m.x - start.lx - start.x) / start.scale;
        const qy = (start.m.y - start.ly - start.y) / start.scale;
        st.x = m.x - start.lx - qx * ns;
        st.y = m.y - start.ly - qy * ns;
        st.scale = ns;
        clampPan();
        apply();
      } else if (e.touches.length === 1 && st.scale > 1) {
        e.preventDefault(); // pan the zoomed image, don't scroll the modal
        st.x = start.x + (e.touches[0].clientX - start.m.x);
        st.y = start.y + (e.touches[0].clientY - start.m.y);
        clampPan();
        apply();
      }
    }, { passive: false });

    content.addEventListener('touchend', (e) => {
      if (e.touches.length > 0) return;
      const c = e.changedTouches[0];
      if (!c) return;
      const now = Date.now();
      const isDbl = now - lastTap.t < DBL_MS && Math.hypot(c.clientX - lastTap.x, c.clientY - lastTap.y) < DBL_PX;
      lastTap = { t: now, x: c.clientX, y: c.clientY };
      if (!isDbl) return;
      const target = e.target.closest('img');
      if (!target && !img) return;
      if (target) setImg(target);
      if (st.scale > 1) {
        st.scale = 1; st.x = 0; st.y = 0;
      } else {
        const r = img.getBoundingClientRect();
        const z = 2.2;
        st.scale = z;
        st.x = -(c.clientX - r.left) * (z - 1);
        st.y = -(c.clientY - r.top) * (z - 1);
        clampPan();
      }
      apply();
      lastTap.t = 0; // a triple tap shouldn't re-trigger
    }, { passive: true });

    // reset zoom whenever any modal closes (Dawn fires this on body from ModalDialog.hide)
    document.body.addEventListener('modalClosed', reset);
  }

  /* ---------- 3) desktop prev/next arrows on the main media viewer ----------
     (2026-07-15) Dawn's "thumbnail" gallery layout has no way to step through
     media on desktop except clicking thumbnails. Arrows overlay the main
     viewer and drive media-gallery's own setActiveMedia (the same path
     thumbnail clicks use), so thumbnail state + variant-hidden media stay
     consistent. Mobile keeps swipe + dots (arrows hidden < 750px, veer.css). */
  const ARROW_SVG =
    '<svg viewBox="0 0 10 16" fill="none" aria-hidden="true" focusable="false">' +
    '<path d="M8.5 1.5 2 8l6.5 6.5" stroke="currentColor" stroke-width="1.8" ' +
    'stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function initArrows(gallery) {
    if (gallery.dataset.veerArrows) return;
    const viewer = gallery.querySelector('slider-component');
    const list = gallery.querySelector('.product__media-list');
    if (!viewer || !list) return;
    if (list.querySelectorAll('.product__media-item').length < 2) return;
    gallery.dataset.veerArrows = 'true';

    // The navigable set = what the thumbnail rail exposes (visible thumbs —
    // respects Dawn's hide_variants filtering). Fallback: all main-list items.
    // NOTE: the desktop "thumbnail" layout CSS-hides every non-active slide in
    // the main list, so filtering main-list items by visibility sees only 1.
    const mediaIds = () => {
      const thumbs = Array.from(gallery.querySelectorAll('.thumbnail-list__item'))
        .filter((li) => li.offsetParent !== null)
        .map((li) => li.querySelector('[data-target]'))
        .filter(Boolean)
        .map((b) => b.dataset.target);
      if (thumbs.length > 1) return thumbs;
      return Array.from(list.querySelectorAll('.product__media-item'))
        .map((li) => li.dataset.mediaId)
        .filter(Boolean);
    };

    const step = (delta) => {
      const ids = mediaIds();
      if (ids.length < 2) return;
      const activeId = list.querySelector('.product__media-item.is-active')?.dataset.mediaId;
      let idx = ids.indexOf(activeId);
      if (idx === -1) idx = 0;
      const nextId = ids[(idx + delta + ids.length) % ids.length];
      // guarded at click time — the media-gallery custom element may upgrade
      // after this script boots (script order isn't guaranteed with defer)
      if (typeof gallery.setActiveMedia === 'function') {
        gallery.setActiveMedia(nextId, false);
      }
    };

    const mk = (dir) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'veer-gallery-arrow veer-gallery-arrow--' + dir;
      b.setAttribute('aria-label', dir === 'prev' ? 'Imaginea anterioară' : 'Imaginea următoare');
      b.innerHTML = ARROW_SVG; // static markup only — no dynamic data
      b.addEventListener('click', (e) => {
        e.preventDefault();
        step(dir === 'next' ? 1 : -1);
      });
      return b;
    };
    viewer.append(mk('prev'), mk('next'));
  }

  function boot() {
    document.querySelectorAll('media-gallery slider-component').forEach(initDots);
    document.querySelectorAll('product-modal').forEach(initZoom);
    document.querySelectorAll('media-gallery').forEach(initArrows);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
